<template>
  <div class="min-h-screen">
    <!-- Hero Section -->
    <section class="hero-section relative h-screen flex items-center justify-center overflow-hidden">
      <!-- Background Video -->
      <div class="absolute inset-0 w-full h-full">
        <video 
          ref="backgroundVideoRef"
          :src="backgroundVideo"
          autoplay
          loop
          muted
          playsinline
          preload="auto"
          class="w-full h-full object-cover"
          @loadedmetadata="handleVideoLoaded"
          @error="handleVideoError"
        ></video>
        <!-- Overlay for better text readability -->
        <div class="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50"></div>
      </div>
      
      <!-- Content Container -->
      <div class="relative z-10 text-center px-4 max-w-6xl mx-auto">
        
        
        <!-- Main Title with Layered Design -->
        <div class="mb-8">
          <h1 class="text-3xl md:text-5xl font-heading tracking-tight mb-4">
            <span class="block text-white/90 font-light">{{ $t('about.hero.titlePart1') }}</span>
            <span class="block font-black text-white drop-shadow-2xl">{{ $t('about.hero.titlePart2') }}</span>
          </h1>
        </div>
        
        <!-- Subtitle with Enhanced Typography -->
        <p class="text-2xl md:text-3xl text-gray-300 max-w-4xl mx-auto mb-10 leading-relaxed font-light text-justify">
          {{ $t('about.hero.subtitle') }}
        </p>
        
        <!-- Stats Preview -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-10">
          <div class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-white mb-2">{{ yearsOfExperience }}+</div>
            <div class="text-sm text-gray-400 uppercase tracking-wide">{{ $t('about.stats.experience') }}</div>
          </div>
          <div class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-white mb-2">15+</div>
            <div class="text-sm text-gray-400 uppercase tracking-wide">{{ $t('about.stats.clients') }}</div>
          </div>
          <div class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-white mb-2">50+</div>
            <div class="text-sm text-gray-400 uppercase tracking-wide">{{ $t('about.stats.projects') }}</div>
          </div>
          <div class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-white mb-2">24/7</div>
            <div class="text-sm text-gray-400 uppercase tracking-wide">{{ $t('about.stats.support') }}</div>
          </div>
        </div>
        
       
      </div>
    </section>

    <!-- Main Content -->
    <main>
      <!-- Company Story -->
      <section class="py-24 bg-gray-900">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <!-- Story Content -->
            <div>
              <span class="text-primary font-semibold text-lg tracking-wide uppercase">{{ $t('about.companyStory.label') }}</span>
              <h2 class="text-3xl md:text-4xl font-heading tracking-tight mt-4 mb-6 font-bold text-white">{{ $t('about.companyStory.title') }}</h2>
              <p class="text-lg text-gray-300 mb-6 text-justify">
                {{ $t('about.companyStory.paragraph1') }}
              </p>
              <p class="text-lg text-gray-300">
                {{ $t('about.companyStory.paragraph2') }}
              </p>
            </div>

            <!-- Timeline -->
            <div class="relative">
              <div class="absolute left-0 top-0 bottom-0 w-1 bg-primary/20"></div>
              <div v-for="(milestone, index) in milestones" :key="index"
                   class="relative pl-8 mb-8">
                <div class="absolute left-0 w-4 h-4 rounded-full bg-primary transform -translate-x-1/2"></div>
                <div class="bg-gray-800 rounded-xl p-6 border border-gray-700">
                  <h3 class="text-xl font-heading mb-2 font-bold text-white">{{ milestone.year }}</h3>
                  <p class="text-gray-300">{{ milestone.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Our Values -->
      <section class="py-24 bg-gray-800">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center mb-16">
            <span class="text-primary font-semibold text-lg tracking-wide uppercase">{{ $t('about.values.label') }}</span>
            <h2 class="text-3xl md:text-4xl font-heading tracking-tight mt-4 font-bold text-white">{{ $t('about.values.title') }}</h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div v-for="(value, index) in values" :key="index"
                 class="bg-gray-800 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-700">
              <div class="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="value.icon"></path>
                </svg>
              </div>
                              <h3 class="text-xl font-heading mb-4 font-bold text-white">{{ value.title }}</h3>
              <p class="text-gray-300">{{ value.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Team Section -->
      <section class="py-24 bg-gray-900">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center mb-16">
            <span class="text-primary font-semibold text-lg tracking-wide uppercase">{{ $t('about.team.label') }}</span>
            <h2 class="text-3xl md:text-4xl font-heading tracking-tight mt-4 font-bold text-white">{{ $t('about.team.title') }}</h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <!-- Managing Director -->
            <div v-if="team.length > 0"
                 class="bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-700">
              <div class="min-h-[500px] max-h-[600px] flex items-center justify-center bg-gray-900 overflow-hidden">
                <img :src="team[0].image" :alt="team[0].name" 
                     class="w-full h-full object-contain max-w-full max-h-full transform transition-transform duration-500">
              </div>
              <div class="p-6">
                <h3 class="text-xl font-heading mb-1 font-bold text-white">{{ team[0].name }}</h3>
                <p class="text-primary mb-4">{{ team[0].position }}</p>
                <p class="text-gray-300 mb-4">{{ team[0].bio }}</p>
                <a v-if="team[0].linkedin" 
                   :href="team[0].linkedin" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   class="inline-flex items-center text-primary">
                  <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <span>LinkedIn</span>
                </a>
              </div>
            </div>

            <!-- Pool Image of Techs Across Germany -->
            <div class="bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-700">
              <div class="min-h-[500px] max-h-[600px] flex items-center justify-center bg-gray-900 overflow-hidden">
                <img :src="poolImageSrc" :alt="$t('about.team.poolImage.title')" 
                     class="w-full h-full object-contain max-w-full max-h-full transform transition-transform duration-500">
              </div>
              <div class="p-6">
                <h3 class="text-xl font-heading mb-2 font-bold text-white">{{ $t('about.team.poolImage.title') }}</h3>
                <p class="text-gray-300">{{ $t('about.team.poolImage.description') }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>



      <!-- Call to Action -->
      <section class="py-24 bg-gray-800">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                      <h2 class="text-3xl md:text-5xl font-heading tracking-tight mb-6 font-bold text-white">{{ $t('about.cta.title') }}</h2>
          <p class="text-xl text-gray-300 mb-8 max-w-2xl mx-auto text-justify">
            {{ $t('about.cta.description') }}
          </p>
          <router-link to="/contact" 
                      class="inline-flex items-center px-8 py-4 text-lg font-medium rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors duration-300">
            {{ $t('about.cta.button') }}
            <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
            </svg>
          </router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import enTranslations from '../i18n/en'
import deTranslations from '../i18n/de'
import backgroundVideo from '../assets/1476238_People_Technology_3840x2160.mp4'
import poolImageSrc from '../assets/vernetzungskonzept-stillleben-arrangement.jpg'
import navedImage from '../assets/ncneu2.jpg'

const { t, locale } = useI18n()
const backgroundVideoRef = ref(null)

// Calculate years of experience from 2022
const startYear = 2022
const currentYear = new Date().getFullYear()
const yearsOfExperience = computed(() => currentYear - startYear + 1)

const handleVideoLoaded = () => {
  console.log('Video loaded successfully')
  if (backgroundVideoRef.value) {
    backgroundVideoRef.value.play().catch(error => {
      console.log('Video autoplay prevented:', error)
    })
  }
}

const handleVideoError = (e) => {
  console.error('Video loading error:', e)
  console.log('Video source:', backgroundVideo)
}

onMounted(() => {
  // Ensure video plays when mounted
  if (backgroundVideoRef.value) {
    const playPromise = backgroundVideoRef.value.play()
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        console.log('Video autoplay prevented:', error)
      })
    }
  }
})

// Helper function to get array translations
const getArrayTranslation = (key) => {
  const keys = key.split('.')
  const currentLocale = locale.value || 'en'
  const messages = currentLocale === 'en' ? enTranslations : deTranslations
  
  let value = messages
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k]
    } else {
      console.warn(`Translation key not found: ${key} at ${k}`)
      return []
    }
  }
  return Array.isArray(value) ? value : []
}

const milestones = computed(() => getArrayTranslation('about.milestones'))

const values = computed(() => {
  const valueItems = getArrayTranslation('about.values.items')
  // Add icons to values (icons stay constant)
  const icons = [
    'M13 10V3L4 14h7v7l9-11h-7z',
    'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
  ]
  return valueItems.map((item, index) => ({
    ...item,
    icon: icons[index] || ''
  }))
})

const team = computed(() => {
  const teamMembers = getArrayTranslation('about.team.members')
  // Add image to managing director (only one member now)
  const images = [
    navedImage
  ]
  return teamMembers.map((member, index) => ({
    ...member,
    image: images[index] || ''
  }))
})


</script>

<style scoped>
/* Custom styles for the about page */
.timeline-item {
  @apply relative pl-8 mb-8;
}

.timeline-dot {
  @apply absolute left-0 w-4 h-4 rounded-full bg-primary transform -translate-x-1/2;
}

.timeline-content {
  @apply bg-gray-800 rounded-xl p-6 border border-gray-700;
}

/* Team Card Styles */
.team-card {
  @apply bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-700;
}

.team-image {
  @apply aspect-[4/3];
}

.team-image img {
  @apply w-full h-full object-cover transform transition-transform duration-500;
}

/* Value Card Styles */
.value-card {
  @apply bg-gray-800 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-700;
}

.value-icon {
  @apply w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6;
}

/* Achievement Styles */
.achievement-item {
  @apply transform hover:scale-105 transition-transform duration-300;
}

.achievement-value {
  @apply text-5xl font-bold mb-3;
}

.achievement-label {
  @apply text-xl;
}

/* CTA Section Styles */
.cta-section {
  @apply py-24 bg-gray-800;
}

.cta-button {
  @apply inline-flex items-center px-8 py-4 text-lg font-medium rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors duration-300;
}
</style> 